import { useEffect, useState } from 'react'
import { ArrowUpRight, LineChart, Users, Wrench } from 'lucide-react'
import { Link } from 'react-router-dom'
import GuideList from '../components/GuideList'
import { siteModules } from '../data/siteModules'
import { loadGuides } from '../lib/guides'
import { GuideSummary } from '../types/guides'

const moduleIcons = {
  Wrench,
  LineChart,
  Users
}

const Home = () => {
  const [guides, setGuides] = useState<GuideSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    loadGuides()
      .then(items => {
        if (!cancelled) setGuides(items)
      })
      .catch(reason => {
        if (!cancelled) {
          setError(reason instanceof Error ? reason.message : '资料列表暂时无法加载')
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-12 md:py-16">
          <p className="text-sm font-medium text-slate-500">Parentool</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl">亲子资料与实用工具</h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">
            记录家庭出行、育儿和日常使用中的实用内容。
          </p>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-10 px-4 py-10 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-16">
        <section aria-labelledby="latest-guides-heading">
          <div className="mb-6 flex items-end justify-between gap-4 border-b border-slate-200 pb-4">
            <h2 id="latest-guides-heading" className="text-2xl font-semibold text-slate-950">最新资料</h2>
            <Link to="/guides/" className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-950">
              查看全部资料
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          {isLoading && <p className="py-10 text-center text-sm text-slate-500" aria-live="polite">正在加载资料...</p>}
          {error && <p className="border-y border-slate-200 py-10 text-center text-sm text-slate-500" role="alert">{error}</p>}
          {!isLoading && !error && <GuideList guides={guides} limit={5} showFilters={false} emptyText="暂无资料" />}
        </section>

        <aside className="border-t border-slate-200 pt-8 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0" aria-labelledby="modules-heading">
          <h2 id="modules-heading" className="text-sm font-semibold uppercase tracking-wider text-slate-500">其他模块</h2>
          <div className="mt-4">
            {siteModules.map(module => {
              const Icon = moduleIcons[module.icon]
              return (
                <Link key={module.id} to={module.href} className="group flex items-start gap-3 border-b border-slate-200 py-4 first:pt-0 last:border-b-0">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1 font-medium text-slate-950 group-hover:text-slate-600">
                      {module.name}
                      <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <span className="mt-1 block text-sm leading-6 text-slate-500">{module.description}</span>
                  </span>
                </Link>
              )
            })}
          </div>
        </aside>
      </main>
    </div>
  )
}

export default Home
